import React from 'react';
import { STATUS_COLORS, STATUS_LABELS } from '../constants/colors';

/**
 * The CONTROLS for one order-list filter group — and the only definition of them
 * anywhere. Rendered by `ui/FiltersMasterDetail`'s detail pane, from
 * `components/OrderFilterBar.js`.
 *
 * 🥇 **THIS IS `components/gags/GagFilterGroupBody.js` TRANSPOSED, AND THE
 * TRANSPOSITION IS THE WHOLE POINT — READ THAT FILE FIRST.** The Gag Breakdown
 * chose this layout on 2026-08-09 and every decision it records applies here:
 * lists rather than dropdowns, REAL `<input type="radio">` for single-value
 * groups and REAL `<input type="checkbox">` for multi-select ones (never
 * `aria-checked` buttons), the `<label>` wrapping its input so the whole row is
 * a click target and the text IS the accessible name, and the coloured status
 * pill kept as the checkbox's LABEL so this does not become the one surface
 * where a status is named but not coloured.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🪤 THE ORDER FILTER MODEL IS THE MIRROR IMAGE OF THE BREAKDOWN'S
 * ═══════════════════════════════════════════════════════════════════════════
 * Order statuses default **ALL-ON**, so an EXCLUSION is what narrows the list.
 * The Breakdown's `DEFAULT_FILTERS.statuses` starts **EMPTY**, so a SELECTION is
 * what narrows there. `CLAUDE.md` states the binding cross-surface rule this
 * follows from — **`✕` must always RELAX a filter, never tighten it** — and
 * warns that three of the app's four describe-functions are the second kind, so
 * the order pages are the exception and the tempting generalisation is wrong.
 * Consequences that live in this file: a **ticked** status box means SHOWN, and
 * the rail's count for this group is the number of statuses **hidden** (see
 * `orderFilterGroupCount` in `OrderFilterBar.js`).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔻 STATUS IS A CHECKBOX LIST, NOT THE STRUCK-THROUGH PILL ROW — 2026-08-10,
 *    AND IT SUPERSEDES A STANDING `CLAUDE.md` RULE WHOSE PREMISE EXPIRED
 * ═══════════════════════════════════════════════════════════════════════════
 * Product owner, offered both: *"yes do both and check boxes"*.
 *
 * The rule it supersedes said the inactive pill must KEEP `line-through`,
 * because *"adopting the breakdown's flat inactive style here inverts the
 * reading of the control"*. **That ruling was correct and its PRECONDITION
 * changed** — it holds while the PILL IS THE ON/OFF CARRIER, and it stops
 * applying the moment a checkbox sits in front of the pill. State is now carried
 * by the tick; the pill is a label, and `STATUS_COLORS` is unchanged, so
 * recognising "Delivered" by its green works exactly as before.
 *
 * 🔑 **The deciding argument, which is worth more than the instance: a
 * strike-through is a convention you must LEARN, a tick is one you READ** — and
 * "ticked = shown" is the correct reading under an all-on default, where
 * "struck-through = hidden" had to be explained by the italic note under the
 * old pill row. **This is not the flat inactive style the old rule forbade**;
 * that style deleted the state signal, this one moves it into a stronger
 * channel.
 *
 * ⚠️ **The `aria-pressed` → `checked` change is a TEST CONTRACT change, not a
 * test repair.** The `${testIdPrefix}-${status}` testids are preserved exactly
 * and now sit on the `<input>`, so every existing click site keeps working; what
 * changed is how state is READ. Say so if you touch those assertions.
 *
 * Props:
 *  - group: `'status'` | `'production'`
 *  - statuses: string[] — the pill order for this page
 *  - selectedStatuses: string[]; onToggleStatus(status)
 *  - statusCounts: optional { [status]: number } — per-status counts
 *  - productionOptions: [{ id, name }]; productionFilter; onProductionFilterChange
 *  - testIdPrefix: the page's prefix. 🚨 A CONTRACT — see `OrderFilterBar.js`.
 */

/**
 * ONE option row. `<label>` wraps the input so the whole row is a click target
 * and the text IS the accessible name — no `aria-label` to drift from what is on
 * screen. Byte-identical to `GagFilterGroupBody`'s `ROW_CLS`; it is not imported
 * from there because that file is the Breakdown's vocabulary and this one must
 * not depend on it. If a third surface needs these, they move to `ui/`.
 */
const ROW_CLS =
  'flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm cursor-pointer ' +
  'text-gray-800 dark:text-gray-200 ' +
  'hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors';

const CONTROL_CLS =
  'shrink-0 h-4 w-4 accent-blue-600 dark:accent-blue-500 cursor-pointer disabled:cursor-not-allowed';

/**
 * 📏 **THE SCROLL CAP — 14rem, the same number `GagFilterGroupBody` measured,
 * and it is load-bearing on THIS surface for a different reason.** A row is
 * `py-1.5` around a `text-sm` line box = 32px, so 14rem (224px) shows seven rows
 * and starts scrolling on the eighth. **Status never reaches it** — there are
 * seven order statuses, which is exactly the cap and deliberately so. The
 * PRODUCTION list is the one that scrolls: the dev database holds **137
 * productions**, and a department's own list is shorter but unbounded.
 *
 * ♿ **No `tabIndex={0}` on the region, deliberately** — that rule is for scroll
 * regions of NON-focusable content. These hold nothing but inputs, so a keyboard
 * user reaches every row by tabbing and the browser scrolls each into view as
 * focus lands; a container stop would land on nothing.
 */
const SCROLL_LIST_CLS = 'max-h-56 overflow-y-auto -mx-1 px-1 overscroll-contain rounded-lg';

export default function OrderFilterGroupBody({
  group,
  statuses = [],
  selectedStatuses = [],
  onToggleStatus,
  statusCounts,
  productionOptions = [],
  productionFilter = 'all',
  onProductionFilterChange,
  /* 🔀 Order Completion (2026-08-16), REDESIGNED the same day from a
     THREE-STATE radiogroup (All / Hide completed / Only completed) to a
     single boolean PILL sitting in this same checkbox list, styled like the
     status pills below it. The "only completed" state is what let unticking
     every received item on a completed order make its own row vanish from
     the list (clear-on-push-back drops `completed_at`, and the "only
     completed" view then excludes the very order the user was looking at) —
     see the block comment in `constants/statuses.js` for the full
     diagnosis. There is no replacement state for it; a boolean is the whole
     design now. OPTIONAL, same additive shape as the production props above
     — omit `onShowCompletedChange` and this row does not render, so
     `Approvals.js` (which never passes it) is unaffected. */
  showCompleted = true,
  onShowCompletedChange,
  testIdPrefix = 'filter',
}) {
  switch (group) {
    // ── MULTI-SELECT. Checkboxes, in the page's own status order.
    // 🪤 A TICK MEANS SHOWN. The set defaults ALL-ON, so an unticked box is an
    // EXCLUSION — see the mirror-image block above before changing anything here.
    case 'status':
      return (
        <div role="group" aria-label="Status filter" data-testid={`${testIdPrefix}-list-status`}>
          {statuses.map((s) => {
            const count = statusCounts ? statusCounts[s] : undefined;
            return (
              <label key={s} className={ROW_CLS}>
                <input
                  type="checkbox"
                  className={CONTROL_CLS}
                  checked={selectedStatuses.includes(s)}
                  onChange={() => onToggleStatus(s)}
                  // 🚨 THE TESTID STAYS ON THE CONTROL and keeps its exact name.
                  // Four sites click it (two client suites, two e2e specs) and a
                  // click on an `<input type="checkbox">` still toggles it, so
                  // the conversion costs them nothing.
                  data-testid={`${testIdPrefix}-${s}`}
                />
                <span className={`min-w-0 truncate px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[s]}`}>
                  {STATUS_LABELS[s] || s}
                </span>
                {count != null && (
                  <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400">{count}</span>
                )}
              </label>
            );
          })}
          {/* Order Completion's "Completed" pill (2026-08-16, redesigned same
              day) — the same checkbox-list row as a status, styled with the
              same rounded-pill label as the ✓ Complete badge it controls
              (`OrderTableView.js`), so it reads as "the
              status filter has one more member: completion". It is NOT a
              status and `isOrderComplete` never joins `ORDER_STATUSES` —
              see the block comment in `constants/statuses.js`. A TICK still
              MEANS SHOWN, matching every status row above it: default
              `true`, unticking HIDES completed orders. */}
          {onShowCompletedChange && (
            <label className={ROW_CLS}>
              <input
                type="checkbox"
                className={CONTROL_CLS}
                checked={showCompleted}
                onChange={() => onShowCompletedChange(!showCompleted)}
                data-testid={`${testIdPrefix}-completed`}
              />
              <span className="min-w-0 truncate px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                Completed
              </span>
            </label>
          )}
          <p className="px-2 pt-2 text-xs text-gray-500 dark:text-gray-400 italic">
            Every status is shown by default; unticking one hides it from the
            list. Status narrows within the All / Actionable view.
          </p>
        </div>
      );

    // ── SINGLE-VALUE. Radios, so the control itself says "one of these".
    //
    // 🔀 **THIS CONTROL WAS LOOSE PAGE FURNITURE UNTIL 2026-08-10.** It was a
    // bare `<select>` in its own `<div className="mb-3">` ABOVE the toolbar on
    // `DepartmentOrders.js` — no label, no chip, no entry in
    // `describeOrderFilters`, and not on the filter bar at all, so the one
    // filter that can silently hide most of the page was the one filter the chip
    // row never reported. Moving it in gives it a home, a chip and the
    // `Clear filters` row.
    //
    // 🔑 **"All Productions" is the empty value `'all'`, a REAL MEMBER of the
    // radio group and not a reset button** — the same shape as the Breakdown's
    // "All sub-departments". `productionFilter` holds a `production_id` AS A
    // STRING, which is why every comparison here is `String(...)`.
    //
    // 🔴 **Admin-only, and that gate is the PAGE's.** This file renders what it
    // is given; `DepartmentOrders.js` decides whether to offer the group at all,
    // exactly as it already decided whether to render the `<select>`. Never
    // re-derive a permission here.
    case 'production':
      return (
        <div
          role="radiogroup"
          aria-label="Filter by production"
          data-testid={`${testIdPrefix}-list-production`}
          className={SCROLL_LIST_CLS}
        >
          {[{ id: 'all', name: 'All productions' }, ...productionOptions].map((p) => (
            <label key={String(p.id)} className={ROW_CLS}>
              <input
                type="radio"
                name={`${testIdPrefix}-production`}
                className={CONTROL_CLS}
                checked={String(productionFilter) === String(p.id)}
                onChange={() => onProductionFilterChange(String(p.id))}
                data-testid={`${testIdPrefix}-production-${p.id}`}
              />
              <span className="min-w-0 truncate">{p.name}</span>
            </label>
          ))}
        </div>
      );

    default:
      return null;
  }
}
